// Detailed window of the dual-tier timeline. Renders month markers, today line, single milestone dots
// with date-above + status-below labels, and ClusterBubble + ClusterPopover for collisions.

import { Fragment, useState, useMemo, useCallback, useRef } from "react";
import { clsx } from "clsx";
import type { Milestone } from "../../../../types";
import {
  deriveMilestoneDisplayStatus,
  type MilestoneDisplayStatusResult,
} from "../../../../utils/deriveMilestoneDisplayStatus";
import { deriveTimelineDotLifecycle } from "../../../../utils/deriveMilestoneDisplayLifecycle";
import { formatDayMonth, formatDayMonthYear } from "../../../../utils/formatShortDate";
import {
  clusterMilestones,
  daysBetween,
  isWithinWindow,
  monthMarkers,
  positionFraction,
  type TimelineEntry,
} from "./timelineGeometry";
import ClusterBubble from "./ClusterBubble";
import ClusterPopover from "./ClusterPopover";
import { dotClass } from "./timelineDotClass";

export interface TimelineActiveWindowProps {
  milestones: readonly Milestone[];
  windowStart: string;
  windowEnd: string;
  today: string;
  /** Number of days until the next future milestone (for the empty-window
   *  state copy). When undefined and milestones is empty, we render the
   *  "no upcoming milestones" variant. */
  daysUntilNext?: number;
  /** Click handler for the empty-window CTA. Parent expands the window. */
  onShowWiderWindow?: () => void;
  /** Activate handler for both single dots and popover rows. */
  onActivate: (milestoneId: string) => void;
}

interface OpenCluster {
  centreIso: string;
  milestones: Milestone[];
}

function emptyCopy(daysUntilNext?: number): string {
  if (typeof daysUntilNext !== "number") {
    return "No upcoming milestones in this window";
  }
  if (daysUntilNext <= 0) {
    return "Next milestone is today";
  }
  return `Next milestone is in ${daysUntilNext} days`;
}

interface DotLabel {
  text: string;
  className: string;
}

const LIFECYCLE_LABEL_CLASS: Record<string, { text: string; className: string }> = {
  upcoming: { text: "Upcoming", className: "text-slate-500 dark:text-slate-400" },
  delivered: { text: "Delivered", className: "text-sky-600 dark:text-sky-400" },
  "partially-accepted": {
    text: "Partially accepted",
    className: "text-amber-600 dark:text-amber-400",
  },
  accepted: { text: "Accepted", className: "text-emerald-600 dark:text-emerald-400" },
  paid: { text: "Paid", className: "text-emerald-600 dark:text-emerald-400" },
};

function singleDotLabel(
  display: MilestoneDisplayStatusResult,
  milestone: Milestone,
  today: string,
): DotLabel {
  if (display.riskOverlay === "late") {
    if (milestone.plannedDate) {
      const days = Math.max(0, daysBetween(milestone.plannedDate, today));
      return {
        text: `${days}d late`,
        className: "text-red-600 dark:text-red-400",
      };
    }
    return { text: "Late", className: "text-red-600 dark:text-red-400" };
  }
  if (display.riskOverlay === "delivered-late") {
    return {
      text: "Delivered late",
      className: "text-amber-600 dark:text-amber-400",
    };
  }
  if (display.riskOverlay === "due-soon") {
    return {
      text: "Due soon",
      className: "text-brand-600 dark:text-brand-400",
    };
  }
  return (
    LIFECYCLE_LABEL_CLASS[display.lifecycle] ?? {
      text: display.lifecycle,
      className: "text-slate-500 dark:text-slate-400",
    }
  );
}

function SingleDot({
  entry,
  today,
  onActivate,
}: {
  entry: Extract<TimelineEntry, { kind: "single" }>;
  today: string;
  onActivate: (id: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const display: MilestoneDisplayStatusResult = deriveMilestoneDisplayStatus(
    entry.milestone,
    today,
  );
  // Override lifecycle for dot colour only — partial+paid renders the split
  // emerald/amber gradient via the "partial-paid" key in DOT_CLASS_FOR_LIFECYCLE.
  const dotDisplay = { ...display, lifecycle: deriveTimelineDotLifecycle(entry.milestone) };
  const label = singleDotLabel(display, entry.milestone, today);
  const left = `${(entry.fraction * 100).toFixed(2)}%`;
  const formattedDate = formatDayMonth(entry.centreIso);
  const formattedDateWithYear = formatDayMonthYear(entry.centreIso);
  return (
    <button
      type="button"
      data-testid={`active-window-single-${entry.milestone.id}`}
      onClick={() => onActivate(entry.milestone.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      aria-label={`Milestone ${entry.milestone.milestoneNumber}${entry.milestone.milestoneSuffix ?? ""}, ${entry.milestone.name}, ${formattedDate}`}
      aria-describedby={hovered ? `milestone-dot-tooltip-${entry.milestone.id}` : undefined}
      style={{ left }}
      className="absolute top-0 bottom-0 flex min-w-[40px] -translate-x-1/2 flex-col items-center bg-transparent focus:outline-hidden focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-brand-400"
    >
      <span
        data-testid={`active-window-dot-${entry.milestone.id}`}
        className={clsx(
          "absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full",
          dotClass(dotDisplay),
        )}
      />
      <span
        data-testid={`active-window-label-${entry.milestone.id}`}
        className={clsx(
          "absolute top-1/2 translate-y-[10px] whitespace-nowrap text-caption font-semibold",
          label.className,
        )}
      >
        {label.text}
      </span>
      {hovered && (
        <div
          data-testid={`milestone-dot-tooltip-${entry.milestone.id}`}
          role="tooltip"
          className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 w-48 rounded-md border border-slate-200 bg-white p-3 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800"
        >
          <p className="text-caption font-semibold text-slate-700 dark:text-slate-200">
            Milestone {entry.milestone.milestoneNumber}
            {entry.milestone.milestoneSuffix ?? ""}
          </p>
          <p className="mt-0.5 text-caption text-slate-900 dark:text-slate-100 truncate">
            {entry.milestone.name}
          </p>
          <p className="mt-0.5 text-caption text-slate-500 dark:text-slate-400">
            {formattedDateWithYear}
          </p>
        </div>
      )}
    </button>
  );
}

export default function TimelineActiveWindow({
  milestones,
  windowStart,
  windowEnd,
  today,
  daysUntilNext,
  onShowWiderWindow,
  onActivate,
}: TimelineActiveWindowProps) {
  const [openCluster, setOpenCluster] = useState<OpenCluster | null>(null);
  // Per-cluster bubble refs so focus returns to the triggering bubble when
  // the popover dismisses (WCAG 2.4.3 — focus order on dialog close).
  const bubbleRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const entries = useMemo(
    () => clusterMilestones(milestones, windowStart, windowEnd),
    [milestones, windowStart, windowEnd],
  );
  const markers = useMemo(
    () => monthMarkers(windowStart, windowEnd),
    [windowStart, windowEnd],
  );
  const todayInWindow = isWithinWindow(today, windowStart, windowEnd);
  const todayFraction = positionFraction(today, windowStart, windowEnd);

  const handleClusterClick = useCallback((entry: TimelineEntry) => {
    if (entry.kind !== "cluster") return;
    setOpenCluster((prev) =>
      prev?.centreIso === entry.centreIso
        ? null
        : { centreIso: entry.centreIso, milestones: entry.milestones },
    );
  }, []);

  const handleClose = useCallback(() => {
    setOpenCluster((prev) => {
      if (prev) {
        // Restore focus to the triggering bubble so keyboard users land
        // back on the cluster they opened. Defer to the next tick so the
        // popover unmounts first.
        const target = bubbleRefs.current[prev.centreIso];
        if (target) {
          window.setTimeout(() => target.focus(), 0);
        }
      }
      return null;
    });
  }, []);

  return (
    <section
      data-testid="timeline-active-window"
      aria-label={`Active window: ${windowStart} to ${windowEnd}, ${milestones.length} milestones visible`}
      className="space-y-2"
    >
      <div className="relative h-[110px] rounded-md border border-slate-200 bg-slate-50/50 dark:border-slate-700 dark:bg-slate-800/40">
        <div
          aria-hidden="true"
          data-testid="active-window-axis-line"
          className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2 bg-slate-200 dark:bg-slate-600"
        />
        {markers.map((marker) => {
          // Render label and tick as direct siblings of the section, each
          // anchored at the marker's fractional x. The previous wrapper used
          // `flex flex-col items-center` plus children with `-translate-x-1/2`,
          // which double-centers absolute children inside a zero-width parent
          // (per CSS Flexbox §4.1) — the label's larger width amplified the
          // offset and visibly slipped left of its tick.
          const left = `${(marker.fraction * 100).toFixed(2)}%`;
          return (
            <Fragment key={marker.iso}>
              <span
                aria-hidden="true"
                data-testid={`active-window-month-label-${marker.iso}`}
                className="absolute bottom-1 -translate-x-1/2 text-caption text-slate-500 dark:text-slate-400"
                style={{ left }}
              >
                {marker.label}
              </span>
              <span
                aria-hidden="true"
                className="absolute top-1/2 h-3 w-px -translate-x-1/2 -translate-y-1/2 bg-slate-300 dark:bg-slate-600"
                style={{ left }}
              />
            </Fragment>
          );
        })}
        {todayInWindow && (
          <>
            <div
              aria-hidden="true"
              data-testid="active-window-today-line"
              className="absolute top-4 bottom-4 w-px -translate-x-1/2 bg-brand-500 dark:bg-brand-400"
              style={{ left: `${(todayFraction * 100).toFixed(2)}%` }}
            />
            <span
              aria-hidden="true"
              className="absolute top-1 -translate-x-1/2 text-caption font-semibold text-brand-600 dark:text-brand-300"
              style={{ left: `${(todayFraction * 100).toFixed(2)}%` }}
            >
              TODAY
            </span>
          </>
        )}
        {entries.map((entry) =>
          entry.kind === "single" ? (
            <SingleDot
              key={entry.milestone.id}
              entry={entry}
              today={today}
              onActivate={onActivate}
            />
          ) : (
            <div
              key={entry.centreIso}
              data-testid={`active-window-cluster-anchor-${entry.centreIso}`}
              className="absolute top-1/2"
              style={{ left: `${(entry.fraction * 100).toFixed(2)}%` }}
            >
              <ClusterBubble
                milestones={entry.milestones}
                centreIso={entry.centreIso}
                today={today}
                open={openCluster?.centreIso === entry.centreIso}
                onActivate={() => handleClusterClick(entry)}
                buttonRef={(node) => {
                  bubbleRefs.current[entry.centreIso] = node;
                }}
              />
              {openCluster?.centreIso === entry.centreIso && (
                <ClusterPopover
                  open
                  milestones={openCluster.milestones}
                  today={today}
                  onActivate={onActivate}
                  onClose={handleClose}
                />
              )}
            </div>
          ),
        )}
        {entries.length === 0 && (
          <div
            data-testid="active-window-empty"
            className="absolute inset-0 flex items-center justify-center gap-2 text-caption text-slate-500 dark:text-slate-400"
          >
            <span>{emptyCopy(daysUntilNext)}</span>
            {onShowWiderWindow && (
              <span aria-hidden="true">·</span>
            )}
            {onShowWiderWindow && (
              <button
                type="button"
                onClick={onShowWiderWindow}
                data-testid="active-window-show-wider"
                className="rounded-md border border-slate-200 bg-white px-2 py-1 text-caption font-medium text-brand-700 hover:bg-slate-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-slate-700 dark:bg-slate-800 dark:text-brand-300 dark:hover:bg-slate-700/40"
              >
                Show wider window
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
